import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useBrandScope } from '../hooks/useBrandScope'
import { BrandEditorView } from '../components/BrandEditorView'
import { BrandTemplateSection } from '../components/BrandTemplateSection'
import { Forbidden } from '../components/common/Forbidden'
import { can } from '../utils/permissions'
import { isForbidden } from '../utils/httpError'
import { loadBrand, saveBrand, createBrand } from '../services/brandStore'
import { notifySuccess, notifyError } from '../utils/notify'
import type { Brand } from '../types'

type LoadStatus = 'loading' | 'ready' | 'forbidden' | 'notfound'

const BRAND_LIST_ROUTE = '/admin/brands'

const EMPTY_BRAND: Brand = { brandKey: '', brandName: '', primaryColor: '#4A6CF7' }

/**
 * Editor für ein Brand-Profil — gemountet an `/admin/brands/:brandKey`,
 * `/admin/brands/new` und `/admin/brand` (Brand-Admin-Shortcut ohne Param,
 * Spec §5.6).
 */
export function BrandEditorPage() {
  const params = useParams<{ brandKey?: string }>()
  const { session } = useAuth()
  const { isAdmin } = useBrandScope()
  const navigate = useNavigate()
  const isNew = params.brandKey === 'new'
  const targetKey = params.brandKey ?? session?.brandKey ?? ''

  const [brand, setBrand] = useState<Brand | null>(null)
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!session) return

    if (isNew) {
      if (!can(session).brandCreate()) {
        setStatus('forbidden')
        return
      }
      setBrand(EMPTY_BRAND)
      setStatus('ready')
      return
    }

    if (!targetKey) {
      setStatus('notfound')
      return
    }

    let cancelled = false
    setStatus('loading')
    loadBrand(targetKey)
      .then((b) => {
        if (cancelled) return
        if (!can(session).brandEdit(b.brandKey)) {
          setStatus('forbidden')
          return
        }
        setBrand(b)
        setStatus('ready')
      })
      .catch((err) => {
        if (!cancelled) setStatus(isForbidden(err) ? 'forbidden' : 'notfound')
      })
    return () => {
      cancelled = true
    }
  }, [isNew, targetKey, session])

  const handleSave = useCallback(
    async (payload: Brand) => {
      setSaving(true)
      try {
        // Backend-DTO verlangt brandKey im Body (auch beim Update) — targetKey aus der
        // URL ist die Wahrheit, nicht der (bei bestehenden Brands ohnehin disabled) Formwert.
        const saved = isNew
          ? await createBrand(payload)
          : await saveBrand(targetKey, { ...payload, brandKey: targetKey })
        notifySuccess(`Brand „${saved.brandName}" gespeichert`)
        if (isAdmin) navigate(BRAND_LIST_ROUTE)
      } catch {
        notifyError('Speichern fehlgeschlagen, bitte erneut.')
      } finally {
        setSaving(false)
      }
    },
    [isNew, targetKey, isAdmin, navigate],
  )

  if (status === 'loading') return statusPanel('Brand-Editor', 'Brand wird geladen …')
  if (status === 'forbidden') return <Forbidden message="Dieses Brand-Profil darfst du nicht bearbeiten." />
  if (status === 'notfound' || !brand || !session) return statusPanel('Nicht gefunden', 'Brand nicht gefunden.')

  return (
    <>
      <BrandEditorView
        brand={brand}
        isNew={isNew}
        isAdmin={isAdmin}
        scopeHint={isAdmin ? 'Admin darf jedes Brand-Profil bearbeiten.' : 'Brand-Admin: nur die eigene Brand.'}
        showBack={isAdmin}
        backTo={BRAND_LIST_ROUTE}
        saving={saving}
        onSave={handleSave}
      />
      {!isNew && status === 'ready' && <BrandTemplateSection brandKey={targetKey} />}
    </>
  )
}

function statusPanel(title: string, text: string) {
  return (
    <section className="panel">
      <div className="head">
        <h2>{title}</h2>
      </div>
      <div className="empty">{text}</div>
    </section>
  )
}
